/**
 * Client charts for the workbench (WB-12).
 *
 * A chart is either calculated and thrown away, or saved as a client profile
 * on the server. The browser keeps no list of its own. A saved client is
 * opened by `profile_id`, so its chart is always built from the coordinates
 * and time zone stored with the profile — never from the place text again.
 */

const SIGNS = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces',
];
const signName = (index) => SIGNS[index - 1] || 'Unknown';

/**
 * The chart engine reads hours and minutes only; "08:05:30" makes it fail.
 * The birth form collects seconds, so they are dropped here, in one place.
 */
export const hhmm = (time) => {
  const m = /^\s*(\d{1,2}):(\d{2})(?::\d{2})?\s*$/.exec(String(time || ''));
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : String(time || '').trim();
};

/** Birth form value -> the body of `POST /api/profiles`. */
export const toProfileBody = (value) => {
  const gender = String(value.gender || '').toLowerCase();
  return {
    relation_type: 'client',
    name: String(value.name || '').trim(),
    dob: value.date,
    tob: hhmm(value.time),
    pob: String(value.place || '').trim(),
    lat: Number(value.latitude),
    lon: Number(value.longitude),
    gender: ['male', 'female', 'other'].includes(gender) ? gender : 'other',
    notes: String(value.notes || '').trim(),
  };
};

/** A stored profile -> the birth details the workbench passes around. */
export const birthDetailsFromProfile = (profile) => ({
  profile_id: profile.profile_id,
  name: String(profile.name || '').trim(),
  date: profile.dob,
  time: profile.tob,
  place: String(profile.pob || '').trim(),
  gender: profile.gender,
  latitude: profile.lat,
  longitude: profile.lon,
  timezone: profile.timezone_name,
});

/**
 * Workbench chart state from a `charts` map. `/divisional-charts-extended` and
 * `/profiles/{id}/chart` return the same per-chart shape, so both go through here.
 */
export const chartStateFromCharts = (birthDetails, divisionalData) => {
  const d1 = divisionalData?.charts?.D1;
  if (!d1?.planets?.length) throw new Error('Invalid chart data');
  const asc = d1.ascendant_sign;
  const houseSigns = d1.house_signs?.length === 12
    ? d1.house_signs.map((s, i) => ({ house: i + 1, sign_index: s, sign_name: signName(s) }))
    : Array.from({ length: 12 }, (_, i) => {
      const si = ((asc - 1 + i) % 12) + 1;
      return { house: i + 1, sign_index: si, sign_name: signName(si) };
    });
  const kundliResult = {
    planets: d1.planets.map((p) => ({
      ...p,
      degrees_in_sign: Math.floor(p.degree_in_sign || 0),
      minutes_in_sign: Math.floor(((p.degree_in_sign || 0) % 1) * 60),
    })),
    ascendant_sign_index: asc,
    ascendant_sign_name: signName(asc),
    ascendant_degrees: Math.floor(d1.ascendant_degree || 0),
    ascendant_minutes: Math.floor(((d1.ascendant_degree || 0) % 1) * 60),
    ascendant_nakshatra: d1.ascendant_nakshatra || 'Unknown',
    ascendant_longitude: d1.ascendant_degree || 0,
    house_signs: houseSigns,
    house_cusps: Array.from({ length: 12 }, (_, i) => ({ house: i + 1, degrees: 0 })),
  };
  return { birthDetails, kundliResult, divisionalData };
};

const authHeaders = (token) => ({ Authorization: `Bearer ${token}` });

const readError = async (res, fallback) => {
  try {
    const data = await res.json();
    const detail = data?.detail;
    if (typeof detail === 'string') return detail;
    if (detail?.message) return detail.message;
  } catch { /* not JSON */ }
  return fallback;
};

/** Save a birth as a client profile. Resolves to the stored profile; throws with the server's reason. */
export const saveClientProfile = async (apiUrl, token, value) => {
  const res = await fetch(`${apiUrl}/profiles`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify(toProfileBody(value)),
  });
  if (!res.ok) throw new Error(await readError(res, 'Could not save this client.'));
  const data = await res.json();
  if (!data?.profile?.profile_id) throw new Error('Could not save this client.');
  return data.profile;
};

/**
 * Correct a saved client's details (WB-15). The server re-stamps coordinates
 * and time zone from what is sent and, when the birth changed, drops every
 * chart cached for that client. Resolves to the stored profile.
 */
export const updateClientProfile = async (apiUrl, token, profileId, value) => {
  const { relation_type: _unchanged, ...body } = toProfileBody(value);
  const res = await fetch(`${apiUrl}/profiles/${encodeURIComponent(profileId)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await readError(res, 'Could not save the changes.'));
  const data = await res.json();
  if (!data?.profile?.profile_id) throw new Error('Could not save the changes.');
  return data.profile;
};

/** True when the edit changes anything a chart is built from. */
export const birthChanged = (before, value) => {
  const next = toProfileBody(value);
  const num = (n) => (n === null || n === undefined || n === '' ? null : Number(Number(n).toFixed(4)));
  return (before.dob || before.date) !== next.dob
    || hhmm(before.tob || before.time) !== next.tob
    || String(before.pob || before.place || '').trim() !== next.pob
    || num(before.lat ?? before.latitude) !== num(next.lat)
    || num(before.lon ?? before.longitude) !== num(next.lon);
};

/** Open a saved client by id: the chart is built from the profile's stored coordinates. */
export const fetchProfileChart = async (apiUrl, token, profileId) => {
  const res = await fetch(`${apiUrl}/profiles/${encodeURIComponent(profileId)}/chart`, {
    headers: authHeaders(token),
  });
  if (!res.ok) throw new Error(await readError(res, 'Could not load this client\'s chart.'));
  const data = await res.json();
  return chartStateFromCharts(birthDetailsFromProfile(data.profile), { charts: data.chart?.charts });
};
